/**
 * States / provinces for destinations where the vendors (Worldfirst / ICL /
 * Atlantic) need a valid state code: USA, Canada, Australia. Mirrors the
 * backend list (BE src/vendors/xpresion/stateCodes.js) — keep them in sync.
 *
 * The booking page shows these as a dropdown ("Tennessee (TN)") and stores the
 * full name in capitals, as the ERP always has; the code is sent to the vendor.
 */
const US_STATES = {
    ALABAMA: "AL", ALASKA: "AK", ARIZONA: "AZ", ARKANSAS: "AR", CALIFORNIA: "CA",
    COLORADO: "CO", CONNECTICUT: "CT", DELAWARE: "DE", "DISTRICT OF COLUMBIA": "DC",
    FLORIDA: "FL", GEORGIA: "GA", HAWAII: "HI", IDAHO: "ID", ILLINOIS: "IL",
    INDIANA: "IN", IOWA: "IA", KANSAS: "KS", KENTUCKY: "KY", LOUISIANA: "LA",
    MAINE: "ME", MARYLAND: "MD", MASSACHUSETTS: "MA", MICHIGAN: "MI",
    MINNESOTA: "MN", MISSISSIPPI: "MS", MISSOURI: "MO", MONTANA: "MT",
    NEBRASKA: "NE", NEVADA: "NV", "NEW HAMPSHIRE": "NH", "NEW JERSEY": "NJ",
    "NEW MEXICO": "NM", "NEW YORK": "NY", "NORTH CAROLINA": "NC",
    "NORTH DAKOTA": "ND", OHIO: "OH", OKLAHOMA: "OK", OREGON: "OR",
    PENNSYLVANIA: "PA", "RHODE ISLAND": "RI", "SOUTH CAROLINA": "SC",
    "SOUTH DAKOTA": "SD", TENNESSEE: "TN", TEXAS: "TX", UTAH: "UT", VERMONT: "VT",
    VIRGINIA: "VA", WASHINGTON: "WA", "WEST VIRGINIA": "WV", WISCONSIN: "WI",
    WYOMING: "WY", "PUERTO RICO": "PR",
};

const CA_PROVINCES = {
    ALBERTA: "AB", "BRITISH COLUMBIA": "BC", MANITOBA: "MB", "NEW BRUNSWICK": "NB",
    "NEWFOUNDLAND AND LABRADOR": "NL", NEWFOUNDLAND: "NL",
    "NORTHWEST TERRITORIES": "NT", "NOVA SCOTIA": "NS", NUNAVUT: "NU",
    ONTARIO: "ON", "PRINCE EDWARD ISLAND": "PE", QUEBEC: "QC",
    SASKATCHEWAN: "SK", YUKON: "YT",
};

const AU_STATES = {
    "NEW SOUTH WALES": "NSW", VICTORIA: "VIC", QUEENSLAND: "QLD",
    "SOUTH AUSTRALIA": "SA", "WESTERN AUSTRALIA": "WA", TASMANIA: "TAS",
    "NORTHERN TERRITORY": "NT", "AUSTRALIAN CAPITAL TERRITORY": "ACT",
};

const STATE_MAPS = { US: US_STATES, CA: CA_PROVINCES, AU: AU_STATES };

// ERP country codes are mostly 3-letter; a few records may hold 2-letter codes.
const COUNTRY_ISO2 = { USA: "US", US: "US", CAN: "CA", CA: "CA", AUS: "AU", AU: "AU" };
const COUNTRY_NAMES = { "UNITED STATES": "US", "UNITED STATES OF AMERICA": "US", USA: "US", CANADA: "CA", AUSTRALIA: "AU" };

const titleCase = (s) =>
    s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()).replace(/\b(And|Of)\b/g, (w) => w.toLowerCase());

/**
 * "US" / "CA" / "AU" for a receiver country that needs a state code, else "".
 * `country` may be only `{ _id }` on a loaded shipment, so the full record is
 * looked up in `countries` when needed.
 */
export function stateCountry(country, countries = []) {
    if (!country) return "";
    const full = country.code || country.name ? country : countries.find((c) => c._id === country._id) || country;
    const byCode = COUNTRY_ISO2[String(full.code || "").trim().toUpperCase()];
    return byCode || COUNTRY_NAMES[String(full.name || "").trim().toUpperCase()] || "";
}

/** Dropdown options for a country: [{ value: "TENNESSEE", code: "TN", label: "Tennessee (TN)" }]. */
export function stateOptions(iso2) {
    const map = STATE_MAPS[iso2];
    if (!map) return [];
    const seen = new Set();
    return Object.entries(map)
        .filter(([, code]) => (seen.has(code) ? false : seen.add(code)))
        .map(([name, code]) => ({ value: name, code, label: `${titleCase(name)} (${code})` }))
        .sort((a, b) => a.label.localeCompare(b.label));
}

/** The option for a stored state (full name or code, any case), or null. */
export function findStateOption(state, iso2) {
    const s = String(state || "").trim().toUpperCase();
    if (!s || !STATE_MAPS[iso2]) return null;
    const code = STATE_MAPS[iso2][s] || s; // name (or alias) -> code
    return stateOptions(iso2).find((o) => o.code === code) || null;
}
